import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface VisibilityConfig {
  showNavbar: boolean;
  showFooter: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationVisibilityService {
  private visibilitySubject = new BehaviorSubject<VisibilityConfig>({
    showNavbar: true,
    showFooter: true
  });

  public visibility$ = this.visibilitySubject.asObservable();

  // Rutas que no deben mostrar navbar ni footer (páginas de autenticación)
  private readonly authRoutes = [
    '/login', '/register', '/forgot-password', '/reset-password',
    '/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'
  ];

  // Rutas que solo ocultan el footer (mantienen navbar)
  private readonly footerHiddenRoutes = [
    // Rutas originales
    '/phase-one', '/phase-two', '/phase-three', '/phase-four', 
    '/phase-five', '/phase-six', '/phase-seven',
    '/principal-menu', 
    '/video-player/1', '/video-player/2', '/video-player/3', '/video-player/4', 
    '/video-player/5', '/video-player/6', '/video-player/7',
    '/payment-options', '/payment-complete', '/checkout', '/checkout-transfer', '/checkout-oxxo',
    
    // Rutas lazy loading
    '/course/phase-one', '/course/phase-two', '/course/phase-three', '/course/phase-four',
    '/course/phase-five', '/course/phase-six', '/course/phase-seven',
    '/course/principal-menu',
    '/course/video-player/1', '/course/video-player/2', '/course/video-player/3', 
    '/course/video-player/4', '/course/video-player/5', '/course/video-player/6', '/course/video-player/7',
    '/payment/payment-options', '/payment/payment-complete', '/payment/checkout', 
    '/payment/checkout-transfer', '/payment/checkout-oxxo'
  ];

  constructor(private router: Router) {
    this.initializeRouteTracking();
  }

  private initializeRouteTracking(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateVisibility(event.url);
      });
  }

  private updateVisibility(currentUrl: string): void {
    // Limpiar parámetros de query para comparación
    const cleanUrl = currentUrl.split('?')[0];
    
    // Verificar si es una ruta de autenticación
    const isAuthRoute = this.authRoutes.some(route => 
      cleanUrl === route || cleanUrl.startsWith(route + '/')
    ) || this.isResetPasswordWithToken(currentUrl);

    // Verificar si debe ocultar el footer
    const isFooterHiddenRoute = this.footerHiddenRoutes.some(route => 
      cleanUrl === route || cleanUrl.startsWith(route + '/')
    );

    const config: VisibilityConfig = {
      showNavbar: !isAuthRoute,
      showFooter: !isAuthRoute && !isFooterHiddenRoute
    };

    this.visibilitySubject.next(config);
    
    // Debug log
    console.log('Navigation Visibility:', {
      url: currentUrl,
      cleanUrl,
      isAuthRoute,
      isFooterHiddenRoute,
      config
    });
  }

  private isResetPasswordWithToken(url: string): boolean {
    return url.includes('reset-password') && url.includes('token=');
  }

  // Método público para obtener la configuración actual
  getCurrentVisibility(): VisibilityConfig {
    return this.visibilitySubject.value;
  }

  // Método para verificar si una ruta es de fase (para transiciones)
  isPhaseRoute(url: string): boolean {
    const phaseRoutes = [
      '/phase-one', '/phase-two', '/phase-three', '/phase-four', 
      '/phase-five', '/phase-six', '/phase-seven',
      '/course/phase-one', '/course/phase-two', '/course/phase-three', '/course/phase-four',
      '/course/phase-five', '/course/phase-six', '/course/phase-seven'
    ];
    return phaseRoutes.some(route => url.includes(route));
  }
}
